import { DollarSign, Calendar, Send, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';

export function DistributeModal({
  open,
  onOpenChange,
  trackTitle,
  formData,
  setFormData,
  tokenHolders,
  totalOwnership,
  onDistribute,
  distributing
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            Distribute Royalties
          </DialogTitle>
          <DialogDescription>
            Distribute earnings to all token holders of "{trackTitle}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Total Amount ($)</label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="e.g., 100.00"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="bg-white/5 border-white/10"
              data-testid="amount-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be split proportionally among {tokenHolders.length} holders ({totalOwnership}% sold)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                <Calendar className="w-4 h-4" /> Period Start
              </label>
              <Input
                type="date"
                value={formData.periodStart}
                onChange={(e) => setFormData({ ...formData, periodStart: e.target.value })}
                className="bg-white/5 border-white/10"
                data-testid="period-start"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                <Calendar className="w-4 h-4" /> Period End
              </label>
              <Input
                type="date"
                value={formData.periodEnd}
                onChange={(e) => setFormData({ ...formData, periodEnd: e.target.value })}
                className="bg-white/5 border-white/10"
                data-testid="period-end"
              />
            </div>
          </div>

          {formData.amount && tokenHolders.length > 0 && (
            <DistributionPreview 
              tokenHolders={tokenHolders}
              totalOwnership={totalOwnership}
              amount={formData.amount}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onDistribute}
            disabled={distributing || !formData.amount || !formData.periodStart || !formData.periodEnd}
            className="bg-green-500 hover:bg-green-600"
            data-testid="confirm-distribute"
          >
            {distributing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Distributing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Distribute ${formData.amount || '0.00'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DistributionPreview({ tokenHolders, totalOwnership, amount }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
      <h4 className="text-sm font-medium mb-3">Distribution Preview</h4>
      <div className="space-y-2">
        {tokenHolders.slice(0, 3).map((holder) => {
          const share = (holder.tokens_owned / totalOwnership) * parseFloat(amount || 0);
          return (
            <div key={holder.user_id} className="flex justify-between text-sm">
              <span className="text-gray-400">
                {holder.user_email || holder.user_id} ({holder.tokens_owned}%)
              </span>
              <span className="text-green-400">${share.toFixed(2)}</span>
            </div>
          );
        })}
        {tokenHolders.length > 3 && (
          <p className="text-xs text-gray-500">
            +{tokenHolders.length - 3} more holders...
          </p>
        )}
      </div>
    </div>
  );
}
